/**
 * Global Run Session Store
 * Persists run state across page navigation and app backgrounding
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface RunSessionState {
  // Session status
  status: 'idle' | 'running' | 'paused' | 'finished';
  sessionId: string | null;
  
  // Run metrics
  duration: number;
  distance: number;
  pace: number;
  calories: number;
  currentSpeed: number;
  
  // Timestamps
  startTime: number | null;
  pausedDuration: number;
  lastSavedAt: number | null;
  
  // Data
  splits: Array<{
    km: number;
    time: number;
    pace: number;
    latitude?: number;
    longitude?: number;
  }>;
  polyline: Array<{ lat: number; lng: number }>;
  
  // ✅ NEW: Elevation data for session recovery
  elevationData: Array<{ distance: number; elevation: number }>;
  currentElevation: number;
  elevationGain: number;
  elevationLoss: number;
  
  // Processing
  processedRunId: string | null;
  
  // BPM data
  avgBpm: number;
  maxBpm: number;
  currentBpm: number;
}

interface RunSessionActions {
  startRun: (sessionId: string) => void;
  pauseRun: () => void;
  resumeRun: () => void;
  finishRun: () => void;
  resetRun: () => void;
  setPartialState: (partial: Partial<RunSessionState>) => void;
  updateMetrics: (metrics: Partial<RunSessionState>) => void;
  setProcessedRunId: (runId: string) => void;
}

const initialState: RunSessionState = {
  status: 'idle',
  sessionId: null,
  duration: 0,
  distance: 0,
  pace: 0,
  calories: 0,
  currentSpeed: 0,
  startTime: null,
  pausedDuration: 0,
  lastSavedAt: null,
  splits: [],
  polyline: [],
  elevationData: [],
  currentElevation: 0,
  elevationGain: 0,
  elevationLoss: 0,
  processedRunId: null,
  avgBpm: 0,
  maxBpm: 0,
  currentBpm: 0,
};

export const useRunSession = create<RunSessionState & RunSessionActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      startRun: (sessionId: string) => {
        set({
          status: 'running',
          sessionId,
          startTime: Date.now(),
          duration: 0,
          distance: 0,
          pace: 0,
          calories: 0,
          splits: [],
          polyline: [],
          pausedDuration: 0,
          processedRunId: null,
          lastSavedAt: Date.now(),
        });
      },

      pauseRun: () => {
        set({
          status: 'paused',
          lastSavedAt: Date.now(),
        });
      },

      resumeRun: () => {
        set({
          status: 'running',
          lastSavedAt: Date.now(),
        });
      },

      finishRun: () => {
        set({
          status: 'finished',
          lastSavedAt: Date.now(),
        });
      },

      resetRun: () => {
        set(initialState);
        // Clear persisted state
        sessionStorage.removeItem('active_run_state');
        sessionStorage.setItem('run_finished', 'true');
      },

      setPartialState: (partial: Partial<RunSessionState>) => {
        set({
          ...partial,
          lastSavedAt: Date.now(),
        });
      },

      updateMetrics: (metrics: Partial<RunSessionState>) => {
        set({
          ...metrics,
          lastSavedAt: Date.now(),
        });
      },

      setProcessedRunId: (runId: string) => {
        set({
          processedRunId: runId,
        });
      },
    }),
    {
      name: 'run-session-storage',
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          if (!str) return null;
          
          const parsed = JSON.parse(str);
          // Validate state values on restore to prevent corruption display
          if (parsed.state) {
            if (parsed.state.duration > 86400 || parsed.state.duration < 0) {
              parsed.state.duration = 0;
            }
            if (parsed.state.distance > 500000 || parsed.state.distance < 0) {
              parsed.state.distance = 0;
            }
            if (parsed.state.startTime && parsed.state.startTime < 1600000000000) {
              parsed.state.startTime = Date.now();
            }
          }
          return parsed;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
          // Also update active_run_state for backward compatibility
          if (value.state.status !== 'idle' && value.state.status !== 'finished') {
            sessionStorage.setItem('active_run_state', JSON.stringify(value.state));
          }
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
          sessionStorage.removeItem('active_run_state');
        },
      },
    }
  )
);
